import {
  PageWrapper,
  RECENTLY_APPROVED_MILESTONE_DASHBOARD_PARAMS,
  UserDisplayName,
} from "@/components";
import DashboardTableFullView from "@/components/Dashboard/DashboardTableFullView";
import { recentlyApprovedMilestoneFieldNames } from "@/enums";
import { routes } from "@/helpers";
import { useColumnFactory } from "@/hooks/useColumnFactory";
import {
  useGetRecentlyApprovedMilestonesQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useQueryParams } from "use-query-params";

const PAGINATION_PAGE_SIZE = 10;

const RecentlyApprovedMilestonesFullView = () => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useQueryParams({
    ...RECENTLY_APPROVED_MILESTONE_DASHBOARD_PARAMS,
  });

  const {
    createCustomSortableSmallerNumColumn,
    createFilterableSortableNumberColumn,
    createFilterableSortableSmallerNameColumn,
    createSortableDateColumn,
    createMilestoneViewLinkColumn,
    createFilterableSortableDropdownColumn,
    createFilterableSortableSmallerUserNameColumn,
    createFilterableCustomSortableNameColumn,
    handleChange,
  } = useColumnFactory(queryParams, setQueryParams);

  const {
    data: { data: projectMilestonesRecentlyApproved, totalCount } = {},
    isFetching,
  } = useGetRecentlyApprovedMilestonesQuery({
    ...queryParams,
    pageSize: PAGINATION_PAGE_SIZE,
  });

  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    const ids = projectMilestonesRecentlyApproved?.map(
      ({ approver }) => approver
    );

    if (!ids?.length) return;

    const uniqueProjectLeaderIds = [...new Set(ids)];
    getUsersByIds(uniqueProjectLeaderIds);
  }, [projectMilestonesRecentlyApproved, getUsersByIds]);

  const getUserDisplay = (userId) => {
    if (!userId) return null;

    const userData = usersByIds?.find(({ id }) => id === userId);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData?.displayName}
        userAccountEnabled={userData?.azureAdAccountEnabled}
        user={userData}
      />
    );
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({
        id: "dashboard.titles.recentlyApprovedMilestones",
      })}
    >
      <DashboardTableFullView
        data={projectMilestonesRecentlyApproved}
        isFetching={isFetching}
        title={intl.formatMessage({
          id: "dashboard.titles.recentlyApprovedMilestones",
        })}
        titleRoute={routes.dashboardFullViews.recentlyApprovedMilestones}
        totalCount={totalCount}
        columns={[
          createMilestoneViewLinkColumn(
            intl.formatMessage({ id: "dashboard.columns.milestone" }),
            intl.formatMessage({ id: "dashboard.columns.view" })
          ),
          createFilterableSortableNumberColumn(
            intl.formatMessage({ id: "dashboard.columns.projectNumber" }),
            recentlyApprovedMilestoneFieldNames.projectNumber
          ),
          createFilterableCustomSortableNameColumn(
            intl.formatMessage({ id: "dashboard.columns.projectName" }),
            recentlyApprovedMilestoneFieldNames.projectName,
            recentlyApprovedMilestoneFieldNames.projectNameNormalized
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.parentMilestone" }),
            recentlyApprovedMilestoneFieldNames.parentMilestoneName
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.milestone" }),
            recentlyApprovedMilestoneFieldNames.milestoneName
          ),
          createSortableDateColumn(
            intl.formatMessage({ id: "dashboard.columns.dateApproved" }),
            recentlyApprovedMilestoneFieldNames.dateMilestoneApproved
          ),
          createFilterableSortableSmallerUserNameColumn(
            intl.formatMessage({ id: "dashboard.columns.approver" }),
            recentlyApprovedMilestoneFieldNames.approver,
            recentlyApprovedMilestoneFieldNames.approverName,
            getUserDisplay
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.schedule" }),
            recentlyApprovedMilestoneFieldNames.scheduleName
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.functionalArea" }),
            recentlyApprovedMilestoneFieldNames.functionalArea,
            recentlyApprovedMilestoneFieldNames.functionalAreaLabel
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.projectRole" }),
            recentlyApprovedMilestoneFieldNames.projectRoleName
          ),
          createCustomSortableSmallerNumColumn(
            intl.formatMessage({ id: "dashboard.columns.days" }),
            recentlyApprovedMilestoneFieldNames.daysAvailable,
            recentlyApprovedMilestoneFieldNames.dateMilestoneApproved
          ),
        ]}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        handleChange={handleChange}
        pageSize={PAGINATION_PAGE_SIZE}
      />
    </PageWrapper>
  );
};

export default RecentlyApprovedMilestonesFullView;
